import { renderOperationProgress, type OperationProgress } from "./operation-progress.ts";

let nextStatusId = 0;

/** Recoverable server operations inside an existing modal, without another popup. */
export function createDialogOperations(dialog: HTMLDialogElement, notify: (message: string, error?: boolean) => void) {
  const statusId = `dialog-operation-status-${++nextStatusId}`;
  let busy = false;
  let failed = false;
  let renderedFocus: HTMLElement | null = null;
  const disabled = new Map<HTMLInputElement | HTMLButtonElement | HTMLSelectElement | HTMLTextAreaElement, boolean>();
  const status = (): HTMLElement => {
    let element = dialog.querySelector<HTMLElement>("[data-operation-status]");
    if (!element) {
      element = dialog.ownerDocument.createElement("p");
      element.className = "dialog-operation-status";
      element.id = statusId;
      element.dataset.operationStatus = "";
      element.setAttribute("role", "status");
      dialog.append(element);
    }
    return element;
  };
  const lock = () => dialog.querySelectorAll<HTMLInputElement | HTMLButtonElement | HTMLSelectElement | HTMLTextAreaElement>("input,button,select,textarea").forEach(control => {
    if (!disabled.has(control)) disabled.set(control, control.disabled);
    control.disabled = true;
  });
  const toast = (message: string, error = false) => {
    failed ||= error;
    const element = status();
    element.setAttribute("role", error ? "alert" : "status");
    element.classList.toggle("is-error", error);
    element.textContent = message;
    // A persistent modal message is sufficient; avoid a second overlay/announcement.
    if (!dialog.isConnected || !dialog.open) notify(message, error);
  };
  const progress = (value: OperationProgress) => { const element = status(); element.classList.remove("is-error"); renderOperationProgress(element, value); };
  async function run(operation: () => void | Promise<void>): Promise<void> {
    if (busy) return;
    busy = true;
    failed = false;
    const focused = dialog.ownerDocument.activeElement as HTMLElement | null;
    renderedFocus = null;
    dialog.setAttribute("aria-busy", "true");
    status().textContent = "Working…";
    status().setAttribute("role", "status");
    status().classList.remove("is-error");
    try {
      // Read FormData synchronously before disabling form controls.
      const pending = operation();
      lock();
      await pending;
    } catch (error) {
      toast(`${error instanceof Error ? error.message : "The request could not be completed."}${error instanceof TypeError ? " Check your connection and existing entries before retrying if the result is uncertain." : ""}`, true);
    } finally {
      busy = false;
      dialog.removeAttribute("aria-busy");
      disabled.forEach((wasDisabled, control) => { control.disabled = wasDisabled; });
      disabled.clear();
      if (status().textContent === "Working…" || !failed && status().querySelector("progress")) status().replaceChildren();
      if (failed) status().scrollIntoView?.({ block: "nearest" });
      if (dialog.isConnected && (!dialog.contains(dialog.ownerDocument.activeElement) || dialog.ownerDocument.activeElement === dialog)) {
        const target = focused?.isConnected ? focused : renderedFocus || dialog.querySelector<HTMLElement>("button:not(:disabled)");
        target?.focus({ preventScroll: true });
      }
    }
  }
  function listen(selector: string, type: string, operation: (event: Event) => void | Promise<void>): void {
    dialog.querySelectorAll(selector).forEach(element => element.addEventListener(type, event => {
      if (type === "submit") event.preventDefault();
      void run(() => operation(event)).then(() => {
        // Retrying a PATCH-backed field change is safe; never automatically replay creates.
        if (failed && type === "change" && element.isConnected && !status().querySelector("button")) {
          element.setAttribute("aria-invalid", "true");
          const descriptions = (element.getAttribute("aria-describedby") || "").split(" ").filter(Boolean);
          if (!descriptions.includes(statusId)) descriptions.push(statusId);
          element.setAttribute("aria-describedby", descriptions.join(" "));
          const retry = dialog.ownerDocument.createElement("button");
          retry.type = "button";
          retry.className = "text-button";
          retry.textContent = "Retry change";
          retry.addEventListener("click", () => {
            (element as HTMLElement).focus({ preventScroll: true });
            element.dispatchEvent(new Event(type, { bubbles: true }));
          });
          status().append(" ", retry);
        } else if (!failed && type === "change") {
          element.removeAttribute("aria-invalid");
          const descriptions = (element.getAttribute("aria-describedby") || "").split(" ").filter(value => value && value !== statusId);
          if (descriptions.length) element.setAttribute("aria-describedby", descriptions.join(" "));
          else element.removeAttribute("aria-describedby");
        }
      });
    }));
  }
  /** Preserve unrelated drafts and one-time secrets when a server-backed list changes. */
  function render(html: string): void {
    const focused = dialog.ownerDocument.activeElement;
    const focusedForm = Array.from(dialog.querySelectorAll("form")).findIndex(form => form.contains(focused));
    const identity = focused?.getAttributeNames().filter(name => name.startsWith("data-") || name === "name").map(name => [name, focused.getAttribute(name)]);
    const disclosures = Array.from(dialog.querySelectorAll("details")).map(element => ({ title: element.querySelector("summary")?.textContent, open: element.open }));
    const forms = Array.from(dialog.querySelectorAll("form")).map(form => Array.from(form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[name]")).map(control => ({ name: control.name, value: control.value, checked: "checked" in control && ["checkbox", "radio"].includes(control.type) ? control.checked : undefined })));
    const preserved = Array.from(dialog.querySelectorAll<HTMLElement>("[data-operation-preserve]")).filter(element => element.childNodes.length);
    dialog.innerHTML = html;
    if (!dialog.hasAttribute("aria-labelledby")) dialog.setAttribute("aria-label", dialog.querySelector("h2")?.textContent || "Settings");
    dialog.querySelectorAll("details").forEach(element => {
      const previous = disclosures.find(item => item.title === element.querySelector("summary")?.textContent);
      if (previous) element.open = previous.open;
    });
    Array.from(dialog.querySelectorAll("form")).forEach((form, index) => {
      const controls = Array.from(form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[name]"));
      for (const entry of forms[index] || []) {
        const control = controls.find(control => control.name === entry.name && (entry.checked === undefined || control.value === entry.value));
        if (!control) continue;
        if (entry.checked !== undefined && "checked" in control) control.checked = entry.checked;
        else control.value = entry.value;
      }
    });
    for (const element of preserved) dialog.querySelector(`[data-operation-preserve="${element.dataset.operationPreserve}"]`)?.replaceWith(element);
    const focusScope = focusedForm >= 0 ? dialog.querySelectorAll("form")[focusedForm] : dialog;
    renderedFocus = identity?.length ? Array.from(focusScope?.querySelectorAll<HTMLElement>("button,input,select,textarea") || []).find(element => element.tagName === focused?.tagName && identity.every(([name, value]) => element.getAttribute(name!) === value)) || null : null;
    if (busy) lock();
  }
  dialog.addEventListener("cancel", event => { if (busy) event.preventDefault(); });
  return { run, listen, render, toast, progress };
}
